import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../hooks/ApiProvider/ApiProvider';
import SharedPDFViewer from './PDFViewer';
import VideoViewer from './VideoViewer';
import ImageViewer from './ImageViewer';
import AudioViewer from './AudioViewer';
import TextViewer from './TextViewer';
import { getContentTypeFromUrl } from './fileTypeUtils';

const SharedPage: React.FC = () => {
  const { getSharedContentURL } = useApi();
  const { type, code } = useParams<{ type: string; code: string }>();
  const [sharedContent, setSharedContent] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const hasCalledApi = useRef<boolean>(false);
  const currentParams = useRef<string>('');

  useEffect(() => {
    const paramsKey = `${type}-${code}`;
    
    // 같은 파라미터로 이미 호출했거나, 파라미터가 없으면 리턴
    if (!type || !code || (hasCalledApi.current && currentParams.current === paramsKey)) {
      return;
    }
    
    // 새로운 파라미터인 경우에만 API 호출
    if (currentParams.current !== paramsKey) {
      hasCalledApi.current = true;
      currentParams.current = paramsKey;
      
      setLoading(true);
      setError(null);
      
      getSharedContentURL(type, code)
        .then((res: any) => {
          setSharedContent(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setError(err);
          setLoading(false);
        });
    }
  }, [type, code, getSharedContentURL]);

  // 콘텐츠 URL 추출 및 파일 타입 감지
  const getContentUrl = () => {
    if (!sharedContent) return null;
    
    // sharedContent가 문자열인 경우 (URL)
    if (typeof sharedContent === 'string') {
      return sharedContent;
    }
    
    // sharedContent가 객체인 경우 URL 필드 찾기
    if (typeof sharedContent === 'object') {
      return sharedContent.url || sharedContent.contentUrl || sharedContent.fileUrl || sharedContent.src;
    }
    
    return null;
  };

  const contentUrl = getContentUrl();
  const fileType = contentUrl ? getContentTypeFromUrl(contentUrl) : 'unknown';

  // 콘텐츠가 로드되었고 URL이 있는 경우 적절한 뷰어 렌더링
  if (sharedContent && contentUrl) {
    switch (fileType) {
      case 'pdf':
        return <SharedPDFViewer url={contentUrl} />;
      case 'video':
        return <VideoViewer url={contentUrl} />;
      case 'image':
        return <ImageViewer url={contentUrl} />;
      case 'audio':
        return <AudioViewer url={contentUrl} />;
      case 'text':
        return <TextViewer url={contentUrl} />;
      default:
        // 알 수 없는 파일 타입인 경우 기본 뷰어 (iframe)
        return (
          <div style={{ 
            width: '100vw', 
            height: '100vh', 
            position: 'fixed',
            top: 0,
            left: 0,
            backgroundColor: '#ffffff'
          }}>
            <iframe
              src={contentUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none'
              }}
              title="Content Viewer"
            />
          </div>
        );
    }
  }

  // 로딩, 에러, 또는 콘텐츠가 없는 경우 기본 UI
  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      backgroundColor: '#ffffff',
      fontFamily: 'Arial, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        {loading ? (
          <div>
            <h1 style={{ color: '#333', marginBottom: '20px' }}>로딩 중...</h1>
            <p style={{ color: '#666', fontSize: '16px' }}>공유 콘텐츠를 불러오는 중입니다.</p>
          </div>
        ) : error ? (
          <div>
            <h1 style={{ color: '#e74c3c', marginBottom: '20px' }}>오류 발생</h1>
            <p style={{ color: '#666', fontSize: '16px' }}>콘텐츠를 불러올 수 없습니다.</p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '10px' }}>
              Type: {type || 'N/A'} | Code: {code || 'N/A'}
            </p>
          </div>
        ) : (
          <div>
            <h1 style={{ color: '#333', marginBottom: '20px' }}>콘텐츠 없음</h1>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Type: {type || 'N/A'}
            </p>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Code: {code || 'N/A'}
            </p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '20px' }}>
              공유할 콘텐츠를 찾을 수 없습니다.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SharedPage;
